import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import "./index.css";

const isFontAwesome = (icon) => typeof icon === "string" && icon.includes("fa-");

export default function StatCard({
    value,
    title,
    caption,
    icon,
    tone = "blue",
    progress = null,
    onClick,
    className = "",
    backgroundColor = null,
    lista = [],
    openOn = "click", // "click" | "hover"
}) {
    const stringValue = String(value ?? "");
    const [mainValue, subValue] = stringValue.split("/");
    const totalFuncs = Array.isArray(lista) ? lista.length : 0;

    const [tipOpen, setTipOpen] = useState(false);
    const [panelPos, setPanelPos] = useState({ left: 0, top: 0 });
    const [hasDOM, setHasDOM] = useState(false); // SSR-safe

    const cardRef = useRef(null);
    const panelRef = useRef(null);

    // calcula posição ao lado do card (viewport)
    const computePos = (clientX, clientY) => {
        const pad = 12;
        const r = cardRef.current?.getBoundingClientRect();
        if (!r) return { left: pad, top: pad };

        let left = (clientX ?? r.right) + pad;
        let top = (clientY ?? r.top) + pad;

        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const w = panelRef.current?.offsetWidth ?? 260;
        const h = panelRef.current?.offsetHeight ?? 200;

        if (left + w + pad > vw) left = Math.max(pad, r.left - w - pad);
        if (top + h + pad > vh) top = Math.max(pad, vh - h - pad);

        return { left, top };
    };

    const openTip = (e) => {
        if (!Array.isArray(lista) || lista.length === 0) return;
        const x = e?.clientX;
        const y = e?.clientY;
        setTipOpen(true);
        // posiciona ao abrir (após o painel existir no DOM)
        setTimeout(() => setPanelPos(computePos(x, y)), 0);
    };

    // Fechar somente ao clicar fora (outside click) ou ESC
    useEffect(() => {
        if (!tipOpen) return;

        const onDown = (ev) => {
            const panel = panelRef.current;
            const card = cardRef.current;
            const t = ev.target;
            if (!panel || !card) return;
            // se clicou dentro do painel ou do card, permanece aberto
            if (panel.contains(t) || card.contains(t)) return;
            setTipOpen(false);
        };

        const onKey = (ev) => {
            if (ev.key === "Escape") setTipOpen(false);
        };

        document.addEventListener("mousedown", onDown, true);
        document.addEventListener("touchstart", onDown, true);
        window.addEventListener("keydown", onKey);

        return () => {
            document.removeEventListener("mousedown", onDown, true);
            document.removeEventListener("touchstart", onDown, true);
            window.removeEventListener("keydown", onKey);
        };
    }, [tipOpen]);

    useEffect(() => setHasDOM(true), []);

    const toggleTip = (e) => {
        if (tipOpen) setTipOpen(false);
        else openTip(e);
    };

    // handlers de abertura (escolha hover/click via prop)
    const detailProps = !totalFuncs
        ? {}
        : openOn === "hover"
            ? { onMouseEnter: openTip, onFocus: openTip }
            : {
                onMouseEnter: toggleTip,
                onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleTip(e);
                    }
                },
            };

    const hasProgress = typeof progress === "number" && isFinite(progress);
    const progressPct = hasProgress ? Math.max(0, Math.min(100, progress * 100)) : 0;

    return (
        <div
            ref={cardRef}
            className={`stat-card stat-card--${tone} ${onClick ? "is-clickable" : ""} ${className}`}
            role={onClick ? "button" : "group"}
            tabIndex={0}
            onClick={onClick}
            style={backgroundColor ? { background: backgroundColor } : undefined}
            {...detailProps}
        >
            <div className="stat-card-icon">
                {isFontAwesome(icon)
                    ? <i className={icon} />
                    : <img src={`/images/${icon}.png`} alt="" className="iconPng" />}
            </div>

            <div className="stat-card-body">
                <div className="stat-card-title">{title}</div>
                <div className="stat-card-value">
                    {mainValue}
                    {subValue !== undefined && (
                        <span className="stat-card-subvalue">/{subValue}</span>
                    )}
                </div>
                {caption && <div className="stat-card-caption">{caption}</div>}
                {hasProgress && (
                    <div className="stat-card-progress">
                        <span style={{ width: `${progressPct}%` }} />
                    </div>
                )}
            </div>

            {/* Tooltip STICKY via portal — não fecha sozinho e pode ser maior */}
            {hasDOM && tipOpen && totalFuncs > 0 &&
                createPortal(
                    <div
                        ref={panelRef}
                        role="dialog"
                        aria-label={`Detalhes de ${title}`}
                        className="stat-card-panel"
                        style={{ left: panelPos.left, top: panelPos.top }}
                    >
                        <div className="stat-card-panel-head">
                            <div className="stat-card-panel-title">{title}</div>
                            <button
                                type="button"
                                onClick={() => setTipOpen(false)}
                                aria-label="Fechar"
                                className="stat-card-panel-close"
                            >
                                ×
                            </button>
                        </div>

                        <div className="stat-card-panel-count">
                            {totalFuncs} funcionário{totalFuncs === 1 ? "" : "s"}
                        </div>

                        <ul className="stat-card-panel-list">
                            {lista.map((f, idx) => (
                                <li key={f.id ?? `${f.nome ?? "func"}-${idx}`} title={f.nome}>
                                    {f.nome ?? "—"}
                                </li>
                            ))}
                        </ul>
                    </div>,
                    document.body
                )
            }
        </div>
    );
}

StatCard.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    caption: PropTypes.string,
    icon: PropTypes.string,
    tone: PropTypes.oneOf(["blue", "pink", "amber", "green", "violet", "cyan", "navy", "neutral"]),
    progress: PropTypes.number,
    onClick: PropTypes.func,
    className: PropTypes.string,
    backgroundColor: PropTypes.string,
    lista: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            nome: PropTypes.string,
            data_nascimento: PropTypes.string,
        })
    ),
    openOn: PropTypes.oneOf(["click", "hover"]),
};
